import React from 'react';
import Button from '../../UI/Button';
import Input from '../../UI/Input';
import Select from '../../UI/Select';
import { GoalStatus } from './GoalStatusBadge';

export interface GoalFormData {
  _id?: string;
  id?: string;
  title: string;
  description?: string;
  targetValue: number;
  currentValue: number;
  unit: string;
  status: GoalStatus;
  outcome?: 'pending' | 'met' | 'exceeded' | 'not_met';
  outcomeNotes?: string;
  conclusion?: string;
  results?: string;
  delayReason?: string;
  postponeReason?: string;
  cancelReason?: string;
  milestoneId?: string;
}

interface Milestone {
  id?: string;
  _id?: string;
  title: string;
}

interface GoalFormProps {
  formData: GoalFormData;
  onFormDataChange: (data: Partial<GoalFormData>) => void;
  milestones?: Milestone[];
  updateNotes?: string;
  onUpdateNotesChange?: (notes: string) => void;
  showUpdateNotes?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
}

const GoalForm: React.FC<GoalFormProps> = ({
  formData,
  onFormDataChange,
  milestones = [],
  updateNotes = '',
  onUpdateNotesChange,
  showUpdateNotes = false,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Save',
}) => {
  const handleChange = (field: keyof GoalFormData, value: any) => {
    onFormDataChange({ [field]: value });
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h4 className="font-medium mb-3">Goal Details</h4>
      <div className="space-y-3">
        <Input
          label="Title"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          required
          placeholder="Goal title"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Goal description"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Target Value"
            type="number"
            value={formData.targetValue.toString()}
            onChange={(e) => handleChange('targetValue', parseFloat(e.target.value) || 0)}
            required
            placeholder="0"
          />
          <Input
            label="Current Value"
            type="number"
            value={formData.currentValue.toString()}
            onChange={(e) => handleChange('currentValue', parseFloat(e.target.value) || 0)}
            placeholder="0"
          />
          <Input
            label="Unit"
            value={formData.unit}
            onChange={(e) => handleChange('unit', e.target.value)}
            required
            placeholder="e.g., people, kg, %"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Status"
            value={formData.status}
            onChange={(e) => handleChange('status', e.target.value as GoalStatus)}
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'reached', label: 'Reached' },
              { value: 'failed', label: 'Failed' },
              { value: 'delayed', label: 'Delayed' },
              { value: 'postponed', label: 'Postponed' },
              { value: 'canceled', label: 'Canceled' },
            ]}
          />
          {milestones.length > 0 && (
            <Select
              label="Connected Milestone (Optional)"
              value={formData.milestoneId || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Convert empty string to null to explicitly clear the milestone
                handleChange('milestoneId', value === '' ? null : value);
              }}
              options={[
                { value: '', label: 'None' },
                ...milestones.map((m) => ({
                  value: m.id || m._id || '',
                  label: m.title,
                })),
              ]}
            />
          )}
        </div>
        {formData.status === 'delayed' && (
          <Input
            label="Delay Reason"
            value={formData.delayReason || ''}
            onChange={(e) => handleChange('delayReason', e.target.value)}
            placeholder="Reason for delay"
          />
        )}
        {formData.status === 'postponed' && (
          <Input
            label="Postpone Reason"
            value={formData.postponeReason || ''}
            onChange={(e) => handleChange('postponeReason', e.target.value)}
            placeholder="Reason for postponement"
          />
        )}
        {formData.status === 'canceled' && (
          <Input
            label="Cancel Reason"
            value={formData.cancelReason || ''}
            onChange={(e) => handleChange('cancelReason', e.target.value)}
            placeholder="Reason for cancellation"
          />
        )}
        {(formData.status === 'reached' || formData.status === 'failed') && (
          <>
            <Select
              label="Outcome"
              value={formData.outcome || 'pending'}
              onChange={(e) => handleChange('outcome', e.target.value)}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'met', label: 'Met' },
                { value: 'exceeded', label: 'Exceeded' },
                { value: 'not_met', label: 'Not Met' },
              ]}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conclusion</label>
              <textarea
                value={formData.conclusion || ''}
                onChange={(e) => handleChange('conclusion', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Final conclusion when reached or failed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Results</label>
              <textarea
                value={formData.results || ''}
                onChange={(e) => handleChange('results', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Results data"
              />
            </div>
            <Input
              label="Outcome Notes"
              value={formData.outcomeNotes || ''}
              onChange={(e) => handleChange('outcomeNotes', e.target.value)}
              placeholder="Additional notes about the outcome"
            />
          </>
        )}
        {showUpdateNotes && onUpdateNotesChange && (
          <Input
            label="Update Notes"
            value={updateNotes}
            onChange={(e) => onUpdateNotesChange(e.target.value)}
            placeholder="Notes about this update (will be added to history)"
          />
        )}
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            onClick={onSubmit}
            disabled={!formData.title || !formData.targetValue || !formData.unit || isLoading}
            isLoading={isLoading}
          >
            {submitLabel}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GoalForm;

